import React, { useCallback, useState, useEffect, useRef } from "react";
import { PropTypes } from "prop-types";
import moment from "moment";
import {
  isEmpty,
  keyBy,
  difference,
  uniq,
  cloneDeep,
  forEach,
  keys,
} from "lodash";

import {
  useGroupTypes,
  useWorkspaceTagConfig,
  useWorkspace,
} from "src/contexts/global/WorkspaceContext";
import { API_DATE_FORMAT } from "src/consts/global";

import { MAX_ITEMS_PER_PAGE } from "src/components/OrgChart/consts";
import { getLoadCount } from "src/components/OrgChart/context/utils/getLoadCount";
import { getDirectTeamCollectionId } from "../hooks/useOrgChartNodesAndEdges/utils";
import { useUpdateTeamStoreListener } from "../hooks/useUpdateTeamStoreListener";
import useUpdateMemberListener from "../hooks/useUpdateMemberListener";
import useGetTeamsForOrgChart, {
  ORG_CHART_STATE_KEYS,
} from "../hooks/useGetTeamsForOrgChart";
import useGetActiveAllocationProjectForOrgChart from "../hooks/useGetActiveAllocationProjectForOrgChart";
import useLoadTeamDataById from "../hooks/useLoadTeamDataById";
import OrgChartContext from "./OrgChartContext";
import { orgChartReducer, getInitialOrgChartState } from "./reducer";
import { ACTION_TYPES } from "./actionTypes";
import { getDiffs } from "./utils/getCalculatedState";

const mergeWithPrevState = (prev, next, isLoading = false) => {
  const mergedTeams = Object.values(next?.teams || {})
    .filter((t) => t?.id)
    .map((team) => {
      const prevTeamData = prev.teams[team?.id] || {};
      return {
        ...prevTeamData, // get old data if exists
        childTeams: [...(team?.childTeams || prevTeamData?.childTeams || [])],
        ...team, // merge new data
        // This is called when data is fetched so we can set this to false
        isLoading,
      };
    });

  const mergedMembers = Object.values(next?.members || {}).map((member) => {
    return {
      ...(prev.members[member?.aggregateId] || {}), // get old data if exists
      ...member, // merge new data
    };
  });

  return {
    teams: { ...prev.teams, ...keyBy(mergedTeams, "id") },
    members: { ...prev.members, ...keyBy(mergedMembers, "aggregateId") },
  };
};

const getNewLoadingState = (state, teamId, pagination, doneLoading) => {
  const viewTeam = state.teams[teamId];
  if (!viewTeam) {
    return state;
  }

  const stateClone = cloneDeep(state);

  // we don't show loading nodes when grouping as it just adds noise to the layout
  const showLoadingNodes = !stateClone.viewOptions.groupMembersBySupplyOrDemand;

  // fetching members?
  if (pagination.fetchMembers) {
    if (doneLoading) {
      // reset loading count
      stateClone.teams[viewTeam.id] = {
        ...stateClone.teams[viewTeam.id],
        membersLoadingCount: 0,
      };
    } else if (showLoadingNodes) {
      // set loading count
      stateClone.teams[viewTeam.id] = {
        ...stateClone.teams[viewTeam.id],
        membersLoadingCount: getLoadCount(
          pagination.memberOffset,
          MAX_ITEMS_PER_PAGE,
          viewTeam.directMemberCount
        ),
      };
    }
  }

  // fetching teams?
  if (pagination.fetchTeams) {
    if (doneLoading) {
      // reset loading count
      stateClone.teams[viewTeam.id] = {
        ...stateClone.teams[viewTeam.id],
        childTeamsLoadingCount: 0,
      };
    } else if (showLoadingNodes) {
      // set loading count
      stateClone.teams[viewTeam.id] = {
        ...stateClone.teams[viewTeam.id],
        childTeamsLoadingCount: getLoadCount(
          pagination.teamOffset,
          MAX_ITEMS_PER_PAGE,
          viewTeam.directChildTeamCount
        ),
      };
    }
  }

  return stateClone;
};

const OrgChartContextProvider = ({ teamId, children }) => {
  const tagConfig = useWorkspaceTagConfig();
  const groupTypes = useGroupTypes();
  const workspace = useWorkspace();
  const workspaceId = workspace?.id;

  const activeAllocationProject = useGetActiveAllocationProjectForOrgChart();
  const alloBaselineDate = activeAllocationProject?.baselineDate;
  const fallbackBaselineDate = alloBaselineDate
    ? moment(alloBaselineDate).format(API_DATE_FORMAT)
    : moment().subtract(1, "year").format(API_DATE_FORMAT);

  const [state, setState] = useState(
    getInitialOrgChartState({ tagConfig, teamId, workspaceId })
  );
  const prevViewOptionsRef = useRef(state?.viewOptions?.showChanges);
  const prevTeamIdRef = useRef(teamId);

  const dispatch = useCallback((action) => {
    setState((prevState) => orgChartReducer(prevState, action));
  }, []);

  const toggleExpandedNode = useCallback((targetTeamId, newCurrentViewId) => {
    setState((prevState) => {
      if (newCurrentViewId) {
        return { ...prevState, expandedNodes: [newCurrentViewId] };
      }

      const isRemoving = prevState.expandedNodes.includes(targetTeamId);
      let expandedNodes = uniq([...prevState.expandedNodes, targetTeamId]);

      const getChildTeamsRecusively = (id) => {
        const node = prevState.teams[id];
        if (!node) {
          return [];
        }

        return node.childTeams
          .map((child) => [child.id, ...getChildTeamsRecusively(child.id)])
          .flat();
      };

      if (isRemoving) {
        const childTeamIds = getChildTeamsRecusively(targetTeamId);

        const nodesToCollapse = [...childTeamIds, targetTeamId]
          .map((id) => [id, getDirectTeamCollectionId(id)])
          .flat();

        expandedNodes = uniq(
          prevState.expandedNodes.filter((id) => !nodesToCollapse.includes(id))
        );
      }
      return { ...prevState, expandedNodes };
    });
  }, []);

  const normalData = useGetTeamsForOrgChart({
    pitDate: null,
    teamId,
    setState,
    stateKey: ORG_CHART_STATE_KEYS.NORMAL,
    state,
  });

  const baselineData = useGetTeamsForOrgChart({
    pitDate:
      state.viewOptions.fromDate?.format(API_DATE_FORMAT) ||
      fallbackBaselineDate,
    teamId,
    setState,
    stateKey: ORG_CHART_STATE_KEYS.DIFF_BASELINE,
    state,
  });

  const nextData = useGetTeamsForOrgChart({
    pitDate: state.viewOptions.toDate?.format(API_DATE_FORMAT) || null,
    teamId,
    setState,
    stateKey: ORG_CHART_STATE_KEYS.DIFF_NEXT,
    state,
  });

  const mergePartialData = useCallback(
    (payload) => dispatch({ type: ACTION_TYPES.MERGE_PARTIAL_DATA, payload }),
    [dispatch]
  );

  const loadTeamDataById = useCallback(
    async (
      id,
      currentViewTeamId,
      pagination = {
        fetchMembers: true,
        memberOffset: 0,
        fetchTeams: true,
        teamOffset: 0,
      }
    ) => {
      // Ensure data loaders are available before proceeding
      if (!normalData || !baselineData || !nextData) {
        return;
      }

      setState((prevState) => {
        return {
          ...getNewLoadingState(prevState, id, pagination, false),
          expandedNodes: uniq([...prevState.expandedNodes, id]),
        };
      });

      const [normal, baseline, next] = await Promise.all([
        normalData.loadSpecificTeamDataById(id, pagination),
        baselineData.loadSpecificTeamDataById(id, pagination),
        nextData.loadSpecificTeamDataById(id, pagination),
      ]);

      // Only skip diff processing if we're not in showChanges mode
      // If showChanges is true, we need to process diff data even if baseline/next are null
      // (they might be null if queries didn't execute, but we should still try to process)
      const isShowingChanges = state?.viewOptions?.showChanges;
      if (
        !isEmpty(normal) &&
        isEmpty(baseline) &&
        isEmpty(next) &&
        !isShowingChanges
      ) {
        setState((prevState) => {
          const { teams, members } = mergeWithPrevState(prevState, normal);

          const currentViewTeam =
            teams?.[currentViewTeamId] || prevState.currentViewTeam;

          const nextState = {
            ...prevState,
            teams,
            members,
            expandedNodes: uniq([...prevState.expandedNodes, id]),
            currentViewTeam,
          };

          return getNewLoadingState(nextState, id, pagination, true);
        });

        return;
      }

      // loadMissingTeamsForDiff
      // Refactor duplicate code/logic
      // If baseline or next are null (queries didn't execute), treat them as empty
      const baselineResult = baseline || { teams: {}, members: {} };
      const nextResult = next || { teams: {}, members: {} };

      let extraBaselineTeams = {};
      let extraNextTeams = {};

      let extraBaselineMembers = {};
      let extraNextMembers = {};

      const baselineIds = Object.keys(baselineResult.teams);
      const nextIds = Object.keys(nextResult.teams);

      const baselineDiff = difference(baselineIds, nextIds);
      const nextDiff = difference(nextIds, baselineIds);
      const missingTeamIds = uniq([...baselineDiff, ...nextDiff]);

      const baselineMemberIds = Object.keys(baselineResult.members);
      const nextMemberIds = Object.keys(nextResult.members);

      const baselineMembersDiff = difference(baselineMemberIds, nextMemberIds);
      const nextMembersDiff = difference(nextMemberIds, baselineMemberIds);
      const missingMemberIds = uniq([
        ...baselineMembersDiff,
        ...nextMembersDiff,
      ]);

      if (missingMemberIds.length) {
        const [missingBaselineMembers, missingNextMembers] = await Promise.all([
          baselineData.loadMissingMembersForDiff(missingMemberIds),
          nextData.loadMissingMembersForDiff(missingMemberIds),
        ]);

        extraBaselineMembers = keyBy(missingBaselineMembers, "aggregateId");
        extraNextMembers = keyBy(missingNextMembers, "aggregateId");
      }

      if (missingTeamIds.length) {
        const [missingBaselineTeams, missingNextTeams] = await Promise.all([
          baselineData.loadMissingTeamsForDiff(missingTeamIds),
          nextData.loadMissingTeamsForDiff(missingTeamIds),
        ]);

        extraBaselineTeams = keyBy(missingBaselineTeams, "id");
        extraNextTeams = keyBy(missingNextTeams, "id");
      }

      const { teams, members } = getDiffs(
        {
          ...baselineResult,
          teams: { ...baselineResult.teams, ...extraBaselineTeams },
          members: { ...baselineResult.members, ...extraBaselineMembers },
        },
        {
          ...nextResult,
          teams: { ...nextResult.teams, ...extraNextTeams },
          members: { ...nextResult.members, ...extraNextMembers },
        },
        state?.teams,
        state?.members
      );

      // store keys for quick lookup
      forEach(teams, (team) => {
        if (!isEmpty(team.diff)) {
          teams[team.id].diffIds = keys(team.diff);
        }
      });

      setState((prevState) => {
        const nextTeams = { ...prevState.teams, ...teams };
        const currentViewTeam =
          teams?.[currentViewTeamId] || prevState.currentViewTeam;

        const nextState = {
          ...prevState,
          teams: nextTeams,
          members: { ...prevState.members, ...members },
          expandedNodes: uniq([...prevState.expandedNodes, id]),
          currentViewTeam,
        };

        return getNewLoadingState(nextState, id, pagination, true);
      });
    },
    [
      normalData,
      baselineData,
      nextData,
      state?.members,
      state?.teams,
      state?.viewOptions?.showChanges,
    ]
  );

  useEffect(() => {
    // TODO: this resets state between view modes, meaning a full reload and pagination reset
    const currentViewOptions = state?.viewOptions?.showChanges;
    const isViewOptionsChange =
      prevViewOptionsRef.current !== currentViewOptions;
    const isTeamChange = prevTeamIdRef.current !== teamId;

    const shouldResetState = isViewOptionsChange || isTeamChange;
    if (shouldResetState) {
      setState((prevState) => ({
        ...prevState,
        teams: {},
        members: {},
        expandedNodes: [teamId],
        expandedPanel: {
          ...prevState.expandedPanel,
          data: {
            ...prevState.expandedPanel?.data,
            selectedEntity: teamId,
          },
        },
      }));
    }

    // Update refs after state update is queued, using the values that were compared
    // These will be the "previous" values for the next effect run
    if (isViewOptionsChange) {
      prevViewOptionsRef.current = currentViewOptions;
    }
    if (isTeamChange) {
      prevTeamIdRef.current = teamId;
    }
  }, [state?.viewOptions?.showChanges, teamId]);

  useLoadTeamDataById(loadTeamDataById, teamId, state);

  useUpdateMemberListener({ mergePartialData });

  useUpdateTeamStoreListener({
    state,
    mergePartialData,
    setState,
    loadTeamDataById,
  });

  return (
    <OrgChartContext.Provider
      value={{
        groupTypes,
        state,
        dispatch,
        toggleExpandedNode,
        mergePartialData,
        teamId,
        setState,
        loadTeamDataById,
        activeAllocationProject,
      }}
    >
      {children}
    </OrgChartContext.Provider>
  );
};

OrgChartContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  teamId: PropTypes.string,
};

export default OrgChartContextProvider;
